import React, { useMemo } from 'react';

import { gql } from '@apollo/client';
import { formatToTimeZone, getLocalTimezone, getTZAbbr } from '@modernloop/shared/datetime';

import { ActivityLogCandidateRsvpTentative_ActivityLogFragment, ResponseStatus } from 'src/generated/mloop-graphql';

import { FCWithFragments } from 'src/components/types';

import ActivityLogBase from 'src/entities/ActivityLog/Helpers/ActivityLogBase';
import ActivityLogContent from 'src/entities/ActivityLog/Helpers/ActivityLogContent';

interface Fragments {
  activityLog: ActivityLogCandidateRsvpTentative_ActivityLogFragment;
}

const ActivityLogTaskCandidateRsvpTentative: FCWithFragments<Fragments> = ({ activityLog }) => {
  const { details } = activityLog;

  // eslint-disable-next-line no-underscore-dangle
  if (!details || details.__typename !== 'ActivityLogCandidateRsvpTentativeDetails') {
    throw new Error('ActivityLogCandidateRsvpTentative: details is not ActivityLogCandidateRsvpTentativeDetails');
  }

  const { startAt, endAt, comment } = details;
  const actionLabel = useMemo(() => {
    const timezone = getLocalTimezone();

    const startDate = formatToTimeZone(startAt, 'MMM d, yyyy', timezone);
    const startTime = formatToTimeZone(startAt, 'h:mm a', timezone);
    const endTime = formatToTimeZone(endAt, 'h:mm a', timezone);

    const timezoneAbbr = getTZAbbr(timezone);

    return {
      key: 'hiring-dashboard-candidates:candidates.activityLog.respondedMaybeForInterviewOn',
      fallback: `responded maybe for their interview on ${startDate} from ${startTime} to ${endTime} ${timezoneAbbr}`,
      values: {
        startDate,
        startTime,
        endTime,
        timezone: timezoneAbbr,
      },
    };
  }, [endAt, startAt]);

  return (
    <ActivityLogBase
      activityLog={activityLog}
      responseStatus={ResponseStatus.Tentative}
      actionLabel={actionLabel.fallback}
      actionLabelKey={actionLabel.key}
      actionLabelValues={actionLabel.values}
    >
      {comment ? <ActivityLogContent content={comment} /> : null}
    </ActivityLogBase>
  );
};

ActivityLogTaskCandidateRsvpTentative.fragments = {
  activityLog: gql`
    ${ActivityLogBase.fragments.activityLog}
    fragment ActivityLogCandidateRsvpTentative_activityLog on ActivityLog {
      id
      details {
        __typename
        ... on ActivityLogCandidateRsvpTentativeDetails {
          __typename
          startAt
          endAt
          comment
        }
      }
      ...ActivityLogBase_activityLog
    }
  `,
};

export default ActivityLogTaskCandidateRsvpTentative;
